'use client'

import React from 'react'
import { useModalInquiryContext } from '@/context/ModalInquiryContext'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

interface BlogContactCTAProps {
	locale: string
}

const BlogContactCTA: React.FC<BlogContactCTAProps> = ({ locale }) => {
	const { openModalInquiry } = useModalInquiryContext()
	const t = useTranslations()

	return (
		<>
			<style dangerouslySetInnerHTML={{__html: `
				@keyframes blog-cta-shimmer {
					0% { background-position: -200% 0; }
					100% { background-position: 200% 0; }
				}
				.blog-cta-button {
					background: linear-gradient(135deg, #1F528E 0%, #2563eb 50%, #1F528E 100%);
					background-size: 200% 100%;
				}
				.blog-cta-button:hover {
					animation: blog-cta-shimmer 2s infinite;
				}
			`}} />
			<div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 rounded-md border-2 border-mainColor/20 shadow-md p-8 md:p-10">
				<div className="flex flex-col items-center gap-6 md:gap-8">
					{/* 第一行：Logo */}
					<div className="flex-shrink-0">
						<Image
							src="/image/plsyy/logo.png"
							alt="Company Logo"
							width={180}
							height={48}
							className="object-contain"
							style={{ width: 'auto', height: 'auto' }}
							priority
						/>
					</div>
					
					{/* 第二行：文案内容 */}
					<div className="flex flex-col gap-3 text-center">
						<h3 className="text-2xl md:text-3xl font-bold text-gray-900">
							{t("blog.Contact Us Title")}
						</h3>
						<p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-6xl mx-auto">
							{t("blog.Contact Us Description")}
						</p>
						<div className="mx-auto flex flex-wrap items-center justify-center md:justify-start gap-4 mt-2">
							<div className="flex items-center gap-2 text-sm text-gray-600">
								<svg className="w-5 h-5 text-mainColor" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
								</svg>
								<span>{t("blog.Quick Response")}</span>
							</div>
							<div className="flex items-center gap-2 text-sm text-gray-600">
								<svg className="w-5 h-5 text-mainColor" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								<span>{t("blog.Expert Support")}</span>
							</div>
							<div className="flex items-center gap-2 text-sm text-gray-600">
								<svg className="w-5 h-5 text-mainColor" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								<span>{t("blog.24/7 Available")}</span>
							</div>
						</div>
					</div>

					{/* 第三行：询盘按钮 */}
					<div className="flex-shrink-0">
						<button
							onClick={() => openModalInquiry(false)}
							className="blog-cta-button group relative px-8 md:px-10 py-4 md:py-5 text-white font-bold text-base md:text-lg rounded-lg shadow-xl hover:shadow-md transform hover:scale-105 transition-all duration-300 overflow-hidden ring-2 ring-mainColor/30 hover:ring-mainColor/60 whitespace-nowrap"
						>
							{/* 闪烁光效 */}
							<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
							{/* 内容 */}
							<span className="relative z-10 flex items-center gap-2">
								<svg 
									className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" 
									fill="none" 
									viewBox="0 0 24 24" 
									stroke="currentColor"
								>
									<path 
										strokeLinecap="round" 
										strokeLinejoin="round" 
										strokeWidth={2.5} 
										d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" 
									/>
								</svg>
								{t("form.Inquiry Now")}
								<svg 
									className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" 
									fill="none" 
									viewBox="0 0 24 24" 
									stroke="currentColor"
								>
									<path 
										strokeLinecap="round" 
										strokeLinejoin="round" 
										strokeWidth={2.5} 
										d="M13 7l5 5m0 0l-5 5m5-5H6" 
									/>
								</svg>
							</span>
						</button>
					</div>
				</div>
			</div>
		</>
	)
}

export default BlogContactCTA
